import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    getAllSubscriptions,
    saveSubscription,
    generateSubscriptionInvoice,
    getPaymentRequests,
    reviewSubscriptionPayment,
    getSubscriptionSettings,
    updateSubscriptionSettings,
    getAllSubscriptionInvoices,
} from "../../services/SubscriptionService";
import SnackBar from "../SnackBar";
import { formatINR, formatDate, formatDateTime, statusMeta, BILLING_CYCLES, formatBillingCycle, normalizeBillingCycle, computeRenewalEndDate, downloadInvoicePdf } from "./subscriptionUtils";
import { uploadSubscriptionAsset } from "./subscriptionAssetUpload";

// Reusable image upload with instant local preview + S3 upload (same pipeline as product images).
const LogoUploader = ({ value, onChange, label, showSnack, prefix = "subscription-logos", previewClass = "h-16 w-16" }) => {
    const [busy, setBusy] = useState(false);
    const [localPreview, setLocalPreview] = useState("");
    const blobRef = useRef(null);
    const errorNotifiedRef = useRef(false);

    // Keep local preview in sync when parent value is set (e.g. after save + reload)
    useEffect(() => {
        if (value) setLocalPreview(value);
    }, [value]);

    useEffect(() => {
        return () => {
            if (blobRef.current) {
                URL.revokeObjectURL(blobRef.current);
                blobRef.current = null;
            }
        };
    }, []);

    const displayUrl = value || localPreview;

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            showSnack && showSnack("Please select an image file", "bg-red-500");
            e.target.value = "";
            return;
        }

        if (blobRef.current) {
            URL.revokeObjectURL(blobRef.current);
        }
        const objectUrl = URL.createObjectURL(f);
        blobRef.current = objectUrl;
        setLocalPreview(objectUrl);
        errorNotifiedRef.current = false;
        setBusy(true);

        try {
            const publicUrl = await uploadSubscriptionAsset(f, prefix);
            onChange(publicUrl);
            setLocalPreview(publicUrl);
            if (blobRef.current) {
                URL.revokeObjectURL(blobRef.current);
                blobRef.current = null;
            }
            showSnack && showSnack(`${label} uploaded — click Save Settings to persist`);
        } catch (err) {
            setLocalPreview(value || "");
            if (blobRef.current) {
                URL.revokeObjectURL(blobRef.current);
                blobRef.current = null;
            }
            const msg = err?.message || err?.error || "Upload failed";
            showSnack && showSnack(msg, "bg-red-500");
            console.error(`[LogoUploader] ${label} upload failed:`, err);
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };

    const handleRemove = () => {
        setLocalPreview("");
        if (blobRef.current) {
            URL.revokeObjectURL(blobRef.current);
            blobRef.current = null;
        }
        onChange("");
    };

    return (
        <div className="flex items-center gap-3">
            <div className={`${previewClass} rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0`}>
                {displayUrl ? (
                    <img
                        src={displayUrl}
                        alt={label}
                        className="max-h-full max-w-full object-contain p-1"
                        onError={() => {
                            if (errorNotifiedRef.current) return;
                            errorNotifiedRef.current = true;
                            showSnack && showSnack("Preview failed — save settings and refresh, or re-upload", "bg-red-500");
                        }}
                    />
                ) : (
                    <span className="text-gray-300 text-xs text-center px-1">No image</span>
                )}
            </div>
            <div className="space-y-1">
                <label className={`inline-block px-3 py-1.5 text-sm rounded-lg cursor-pointer ${busy ? "bg-gray-200 text-gray-500 pointer-events-none" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}>
                    {busy ? "Uploading..." : displayUrl ? "Replace image" : `Upload ${label}`}
                    <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={onPick} />
                </label>
                {displayUrl && !busy && (
                    <button type="button" onClick={handleRemove} className="block text-xs text-rose-500 hover:text-rose-700">
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
};

const FEATURE_CATALOG = [
    { key: "pos", name: "POS (Point of Sale)", price: 1500 },
    { key: "delivery_app", name: "Delivery App", price: 1000 },
    { key: "shiprocket", name: "Shiprocket Shipping", price: 800 },
    { key: "zuntly_brain", name: "Zuntly Brain AI", price: 1200 },
    { key: "zuntly_ai", name: "Zuntly AI Revenue Intelligence", price: 1100 },
    { key: "geoiq", name: "Zuntly GeoIQ Intelligence", price: 900 },
    { key: "free_cash", name: "Free Cash Wallet", price: 700 },
    { key: "whatsapp", name: "WhatsApp Notifications", price: 500 },
    { key: "extra_branch", name: "Additional Branch", price: 600 },
];

const emptyDraft = () => ({
    planName: "Custom Plan",
    billingCycle: "monthly",
    basePrice: 0,
    features: [],
    gstEnabled: false,
    gstRate: 18,
    gracePeriodDays: 0,
    reminderDays: 7,
    startDate: "",
    endDate: "",
    autoEndDate: true,
    notes: "",
    clientLogoUrl: "",
});

const SubscriptionManager = () => {
    const [section, setSection] = useState("clients");
    const [snack, setSnack] = useState({ msg: "", visible: false, color: undefined });

    const showSnack = useCallback((msg, color) => {
        setSnack({ msg, visible: true, color });
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
                {[
                    { id: "clients", label: "Clients & Plans" },
                    { id: "approvals", label: "Payment Approvals" },
                    { id: "invoices", label: "Invoices" },
                    { id: "settings", label: "Billing Settings" },
                ].map((s) => (
                    <button
                        key={s.id}
                        onClick={() => setSection(s.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${section === s.id
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {section === "clients" && <ClientsAndPlans showSnack={showSnack} />}
            {section === "approvals" && <PaymentApprovals showSnack={showSnack} />}
            {section === "invoices" && <InvoicesList showSnack={showSnack} />}
            {section === "settings" && <BillingSettings showSnack={showSnack} />}

            <SnackBar
                message={snack.msg}
                isVisible={snack.visible}
                color={snack.color}
                onClose={() => setSnack((p) => ({ ...p, visible: false }))}
            />
        </div>
    );
};

/* ============================ CLIENTS & PLANS ============================ */
const ClientsAndPlans = ({ showSnack }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // master row being edited
    const [draft, setDraft] = useState(emptyDraft());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllSubscriptions();
            if (res.success) setRows(res.data || []);
            else showSnack("Failed to load clients", "bg-red-500");
        } catch (e) {
            showSnack("Error loading clients", "bg-red-500");
        } finally {
            setLoading(false);
        }
    }, [showSnack]);

    useEffect(() => { load(); }, [load]);

    const openEditor = (row) => {
        const sub = row.subscription;
        if (sub) {
            setDraft({
                planName: sub.planName || "Custom Plan",
                billingCycle: normalizeBillingCycle(sub.billingCycle) || "monthly",
                basePrice: sub.basePrice || 0,
                features: (sub.features || []).map((f) => ({ ...f })),
                gstEnabled: !!sub.gstEnabled,
                gstRate: sub.gstRate != null ? sub.gstRate : 18,
                gracePeriodDays: sub.gracePeriodDays || 0,
                reminderDays: sub.reminderDays != null ? sub.reminderDays : 7,
                startDate: sub.startDate ? new Date(sub.startDate * 1000).toISOString().slice(0, 10) : "",
                endDate: sub.endDate ? new Date(sub.endDate * 1000).toISOString().slice(0, 10) : "",
                autoEndDate: false,
                notes: sub.notes || "",
                clientLogoUrl: sub.clientLogoUrl || "",
            });
        } else {
            setDraft(emptyDraft());
        }
        setEditing(row);
    };

    const computeTotals = () => {
        const featureTotal = (draft.features || []).filter((f) => f.enabled).reduce((s, f) => s + (Number(f.price) || 0), 0);
        const subtotal = (Number(draft.basePrice) || 0) + featureTotal;
        const gstAmount = draft.gstEnabled ? (subtotal * (Number(draft.gstRate) || 0)) / 100 : 0;
        return { subtotal, gstAmount, total: subtotal + gstAmount };
    };

    const handleSave = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            const payload = {
                masterId: editing.masterId,
                planName: draft.planName,
                billingCycle: draft.billingCycle,
                basePrice: Number(draft.basePrice) || 0,
                features: draft.features,
                gstEnabled: draft.gstEnabled,
                gstRate: Number(draft.gstRate) || 0,
                gracePeriodDays: Number(draft.gracePeriodDays) || 0,
                reminderDays: Number(draft.reminderDays) || 7,
                notes: draft.notes,
                autoEndDate: draft.autoEndDate,
                clientLogoUrl: draft.clientLogoUrl || "",
            };
            if (draft.startDate) payload.startDate = Math.floor(new Date(draft.startDate).getTime() / 1000);
            if (!draft.autoEndDate && draft.endDate) payload.endDate = Math.floor(new Date(draft.endDate).getTime() / 1000);

            const res = await saveSubscription(payload);
            if (res.success) {
                showSnack("Subscription saved successfully");
                setEditing(null);
                load();
            } else {
                showSnack(res.error || "Failed to save", "bg-red-500");
            }
        } catch (e) {
            showSnack(e.message || "Error saving subscription", "bg-red-500");
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateInvoice = async (row) => {
        try {
            const res = await generateSubscriptionInvoice({ masterId: row.masterId });
            if (res.success) {
                showSnack(`Invoice ${res.data.invoiceNumber} — downloading PDF`);
                downloadInvoicePdf(res.data);
            } else {
                showSnack(res.error || "Failed to generate invoice", "bg-red-500");
            }
        } catch (e) {
            showSnack(e.message || "Error generating invoice", "bg-red-500");
        }
    };

    const toggleCatalogFeature = (cat) => {
        setDraft((d) => {
            const exists = d.features.find((f) => f.key === cat.key);
            if (exists) return { ...d, features: d.features.filter((f) => f.key !== cat.key) };
            return { ...d, features: [...d.features, { ...cat, enabled: true }] };
        });
    };

    const updateFeature = (idx, patch) => {
        setDraft((d) => ({ ...d, features: d.features.map((f, i) => (i === idx ? { ...f, ...patch } : f)) }));
    };

    const addCustomFeature = () => {
        setDraft((d) => ({ ...d, features: [...d.features, { key: `custom_${Date.now()}`, name: "", price: 0, enabled: true }] }));
    };

    const removeFeature = (idx) => setDraft((d) => ({ ...d, features: d.features.filter((_, i) => i !== idx) }));

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const totals = computeTotals();

    return (
        <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {rows.map((row) => {
                    const meta = statusMeta(row.status?.state);
                    return (
                        <div key={row.masterId} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start gap-3">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">{row.branchName}</h3>
                                    <p className="text-sm text-gray-500">{row.ownerName} · {row.email}</p>
                                </div>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.text}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}></span>
                                    {meta.label}
                                </span>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <p className="text-gray-400 text-xs">Plan</p>
                                    <p className="font-medium text-gray-800">{row.subscription?.planName || "—"}</p>
                                </div>
                                <div>
                                    <p className="text-gray-400 text-xs">Amount / cycle</p>
                                    <p className="font-medium text-gray-800">{row.subscription ? formatINR(row.subscription.totalAmount) : "—"}</p>
                                </div>
                                <div>
                                    <p className="text-gray-400 text-xs">Billing cycle</p>
                                    <p className="font-medium text-gray-800">{formatBillingCycle(row.subscription?.billingCycle)}</p>
                                </div>
                                <div>
                                    <p className="text-gray-400 text-xs">Expires</p>
                                    <p className="font-medium text-gray-800">{row.subscription ? formatDate(row.subscription.endDate) : "—"}</p>
                                </div>
                            </div>

                            {row.pendingPayments > 0 && (
                                <div className="mt-3 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                    {row.pendingPayments} payment(s) awaiting verification
                                </div>
                            )}

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button onClick={() => openEditor(row)} className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                                    {row.subscription ? "Edit Plan" : "Configure Plan"}
                                </button>
                                {row.subscription && (
                                    <button onClick={() => handleGenerateInvoice(row)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                                        Download Invoice PDF
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {rows.length === 0 && <p className="text-gray-500">No master accounts found.</p>}
            </div>

            {/* Plan editor drawer */}
            {editing && (
                <div className="fixed inset-0 z-[100] flex justify-end bg-black/40" onClick={() => !saving && setEditing(null)}>
                    <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Configure Subscription</h3>
                                <p className="text-sm text-gray-500">{editing.branchName}</p>
                            </div>
                            <button onClick={() => !saving && setEditing(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Plan Name">
                                    <input className="zn-input" value={draft.planName} onChange={(e) => setDraft({ ...draft, planName: e.target.value })} />
                                </Field>
                                <Field label="Billing Cycle">
                                    <select className="zn-input" value={draft.billingCycle} onChange={(e) => setDraft({ ...draft, billingCycle: e.target.value })}>
                                        {BILLING_CYCLES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Base Price (₹)">
                                    <input type="number" className="zn-input" value={draft.basePrice} onChange={(e) => setDraft({ ...draft, basePrice: e.target.value })} />
                                </Field>
                                <Field label="Grace Period (days after expiry)">
                                    <input type="number" min="0" className="zn-input" value={draft.gracePeriodDays} onChange={(e) => setDraft({ ...draft, gracePeriodDays: e.target.value })} />
                                </Field>
                                <Field label="Reminder Window (days before expiry)">
                                    <input type="number" min="0" className="zn-input" value={draft.reminderDays} onChange={(e) => setDraft({ ...draft, reminderDays: e.target.value })} />
                                </Field>
                                <Field label="Start Date">
                                    <input type="date" className="zn-input" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
                                </Field>
                            </div>

                            <div className="flex items-center gap-3">
                                <input id="autoEnd" type="checkbox" checked={draft.autoEndDate} onChange={(e) => setDraft({ ...draft, autoEndDate: e.target.checked })} className="h-4 w-4" />
                                <label htmlFor="autoEnd" className="text-sm text-gray-700">Auto-calculate expiry from start date + billing cycle</label>
                            </div>
                            {!draft.autoEndDate && (
                                <Field label="Expiry Date">
                                    <input type="date" className="zn-input" value={draft.endDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} />
                                </Field>
                            )}

                            {/* Client branding */}
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Client Logo (shown on invoices)</h4>
                                <LogoUploader label="Client Logo" value={draft.clientLogoUrl} onChange={(url) => setDraft({ ...draft, clientLogoUrl: url })} showSnack={showSnack} />
                            </div>

                            {/* Features */}
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Custom Feature Billing</h4>
                                <p className="text-xs text-gray-500 mb-3">Quick-add common features, then adjust the price per client.</p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {FEATURE_CATALOG.map((cat) => {
                                        const active = draft.features.find((f) => f.key === cat.key);
                                        return (
                                            <button key={cat.key} onClick={() => toggleCatalogFeature(cat)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"}`}>
                                                {cat.name} · {formatINR(cat.price)}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="space-y-2">
                                    {draft.features.map((f, idx) => (
                                        <div key={f.key || idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                                            <input type="checkbox" checked={f.enabled !== false} onChange={(e) => updateFeature(idx, { enabled: e.target.checked })} className="h-4 w-4" />
                                            <input className="zn-input flex-1" placeholder="Feature name" value={f.name} onChange={(e) => updateFeature(idx, { name: e.target.value })} />
                                            <div className="relative w-32">
                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                                                <input type="number" className="zn-input pl-6" value={f.price} onChange={(e) => updateFeature(idx, { price: e.target.value })} />
                                            </div>
                                            <button onClick={() => removeFeature(idx)} className="text-rose-500 hover:text-rose-700 px-2">✕</button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={addCustomFeature} className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium">+ Add custom feature</button>
                            </div>

                            {/* GST */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <input id="gst" type="checkbox" checked={draft.gstEnabled} onChange={(e) => setDraft({ ...draft, gstEnabled: e.target.checked })} className="h-4 w-4" />
                                        <label htmlFor="gst" className="text-sm font-medium text-gray-700">Apply GST</label>
                                    </div>
                                    {draft.gstEnabled && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500">Rate %</span>
                                            <input type="number" className="zn-input w-24" value={draft.gstRate} onChange={(e) => setDraft({ ...draft, gstRate: e.target.value })} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Field label="Notes (internal)">
                                <textarea className="zn-input" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                            </Field>

                            {/* Live total preview */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
                                <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>{formatINR(totals.subtotal)}</span></div>
                                {draft.gstEnabled && <div className="flex justify-between text-sm text-gray-600 mt-1"><span>GST ({draft.gstRate || 0}%)</span><span>{formatINR(totals.gstAmount)}</span></div>}
                                <div className="flex justify-between text-lg font-bold text-gray-900 mt-2 pt-2 border-t border-blue-200"><span>Total / cycle</span><span>{formatINR(totals.total)}</span></div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
                            <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                                {saving ? "Saving..." : "Save Subscription"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ============================ PAYMENT APPROVALS ============================ */
const PaymentApprovals = ({ showSnack }) => {
    const [filter, setFilter] = useState("pending");
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [acting, setActing] = useState(false);
    const [reviewNote, setReviewNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPaymentRequests(filter);
            if (res.success) setPayments(res.data || []);
        } catch (e) {
            showSnack("Error loading payments", "bg-red-500");
        } finally {
            setLoading(false);
        }
    }, [filter, showSnack]);

    useEffect(() => { load(); }, [load]);

    const handleReview = async (payment, action) => {
        setActing(true);
        try {
            const res = await reviewSubscriptionPayment({ paymentId: payment._id, action, reviewNote, extendFromExpiry: true });
            if (res.success) {
                const renewed = res.data?.renewal?.newEndDate;
                showSnack(
                    action === "approve"
                        ? renewed
                            ? `Payment approved — new expiry: ${formatDate(renewed)}`
                            : "Payment approved & subscription activated"
                        : "Payment rejected"
                );
                setPreview(null);
                setReviewNote("");
                load();
            } else {
                showSnack(res.error || "Action failed", "bg-red-500");
            }
        } catch (e) {
            showSnack(e.message || "Error", "bg-red-500");
        } finally {
            setActing(false);
        }
    };

    return (
        <div>
            <div className="flex gap-2 mb-4">
                {["pending", "approved", "rejected", "all"].map((f) => (
                    <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-sm capitalize ${filter === f ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{f}</button>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div></div>
            ) : payments.length === 0 ? (
                <p className="text-gray-500 py-8 text-center">No {filter !== "all" ? filter : ""} payment requests.</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {payments.map((p) => (
                        <div key={p._id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="aspect-video bg-gray-100 cursor-pointer relative" onClick={() => setPreview(p)}>
                                {p.screenshotUrl ? (
                                    <img src={p.screenshotUrl} alt="payment" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">No screenshot</div>
                                )}
                                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${p.status === "pending" ? "bg-amber-100 text-amber-700" : p.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{p.status}</span>
                            </div>
                            <div className="p-4 space-y-1">
                                <h4 className="font-semibold text-gray-900">{p.master?.branchName || p.branchName || "—"}</h4>
                                <p className="text-sm text-gray-500">{p.payerName || p.submittedByName}</p>
                                <p className="text-lg font-bold text-gray-900">{formatINR(p.amount)}</p>
                                <p className="text-xs text-gray-400">{formatDateTime(p.metaData?.createdAt)}</p>
                                {p.referenceNote && <p className="text-xs text-gray-500 italic">"{p.referenceNote}"</p>}
                                <button onClick={() => setPreview(p)} className="mt-2 w-full px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">Review</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {preview && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => !acting && setPreview(null)}>
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900">Verify Payment</h3>
                            <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                            <div className="bg-gray-100 rounded-xl overflow-hidden">
                                {preview.screenshotUrl ? (
                                    <a href={preview.screenshotUrl} target="_blank" rel="noopener noreferrer">
                                        <img src={preview.screenshotUrl} alt="payment proof" className="w-full object-contain max-h-[60vh]" />
                                    </a>
                                ) : <div className="p-10 text-center text-gray-400">No screenshot uploaded</div>}
                            </div>
                            <div className="space-y-3 text-sm">
                                <Detail label="Client" value={preview.master?.branchName || preview.branchName} />
                                <Detail label="Owner" value={preview.master?.ownerName} />
                                <Detail label="Submitted by" value={`${preview.submittedByName || ""} (${preview.submittedByEmail || ""})`} />
                                <Detail label="Amount claimed" value={formatINR(preview.amount)} />
                                <Detail label="Plan amount" value={preview.subscription ? formatINR(preview.subscription.totalAmount) : "—"} />
                                <Detail label="Billing cycle" value={formatBillingCycle(preview.subscription?.billingCycle)} />
                                <Detail label="Current expiry" value={preview.subscription?.endDate ? formatDate(preview.subscription.endDate) : "—"} />
                                {preview.status === "pending" && preview.subscription && (
                                    <Detail
                                        label="New expiry after approve"
                                        value={formatDate(computeRenewalEndDate(preview.subscription, true))}
                                    />
                                )}
                                <Detail label="Reference" value={preview.referenceNote || "—"} />
                                <Detail label="Submitted at" value={formatDateTime(preview.metaData?.createdAt)} />
                                {preview.status !== "pending" && <Detail label="Reviewed" value={`${preview.status} · ${formatDateTime(preview.reviewedAt)}`} />}

                                {preview.status === "pending" && (
                                    <>
                                        <div className="pt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                                            Verify this amount against your real bank / UPI account before approving.
                                            {preview.subscription?.endDate && Number(preview.subscription.endDate) > Math.floor(Date.now() / 1000) ? (
                                                <span> Early payment extends from the current expiry date — unused days are not lost.</span>
                                            ) : (
                                                <span> Payment after expiry extends from the approval date.</span>
                                            )}
                                        </div>
                                        <textarea className="zn-input" rows={2} placeholder="Review note (optional)" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} />
                                        <div className="flex gap-3 pt-1">
                                            <button disabled={acting} onClick={() => handleReview(preview, "approve")} className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{acting ? "..." : "Approve & Activate"}</button>
                                            <button disabled={acting} onClick={() => handleReview(preview, "reject")} className="flex-1 px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">{acting ? "..." : "Reject"}</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ============================ INVOICES ============================ */
const InvoicesList = ({ showSnack }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await getAllSubscriptionInvoices();
                if (res.success) setInvoices((res.data || []).filter((i) => i.status === "paid"));
            } catch (e) {
                showSnack("Error loading invoices", "bg-red-500");
            } finally {
                setLoading(false);
            }
        })();
    }, [showSnack]);

    if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div></div>;
    if (invoices.length === 0) return <p className="text-gray-500 py-8 text-center">No paid subscription invoices yet. Invoices are saved when client payments are approved.</p>;

    return (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
            <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left">
                    <tr>
                        <th className="px-4 py-3 font-medium">Invoice #</th>
                        <th className="px-4 py-3 font-medium">Client</th>
                        <th className="px-4 py-3 font-medium">Issued</th>
                        <th className="px-4 py-3 font-medium">Total</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-right">Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {invoices.map((inv) => (
                        <tr key={inv._id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-800">{inv.invoiceNumber}</td>
                            <td className="px-4 py-3 text-gray-600">{inv.client?.branchName || "—"}</td>
                            <td className="px-4 py-3 text-gray-600">{formatDate(inv.issueDate)}</td>
                            <td className="px-4 py-3 font-medium text-gray-800">{formatINR(inv.total)}</td>
                            <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{inv.status}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                                <button onClick={() => downloadInvoicePdf(inv)} className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">Download PDF</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/* ============================ BILLING SETTINGS ============================ */
const BillingSettings = ({ showSnack }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await getSubscriptionSettings();
                if (res.success) setSettings(res.data);
            } catch (e) {
                showSnack("Error loading settings", "bg-red-500");
            } finally {
                setLoading(false);
            }
        })();
        // Load once on mount — must not re-fetch when snack toasts fire (that wiped unsaved uploads)
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                company: settings.company || {},
                upiId: settings.upiId || '',
                upiName: settings.upiName || '',
                qrImageUrl: settings.qrImageUrl || '',
                bankDetails: settings.bankDetails || {},
                defaultGracePeriodDays: settings.defaultGracePeriodDays ?? 0,
                defaultReminderDays: settings.defaultReminderDays ?? 7,
                supportEmail: settings.supportEmail || '',
                supportPhone: settings.supportPhone || '',
                activationSlaText: settings.activationSlaText || '',
                invoicePrefix: settings.invoicePrefix || 'ZUN',
            };
            const res = await updateSubscriptionSettings(payload);
            if (res.success) {
                showSnack("Settings saved");
                setSettings(res.data);
            } else {
                showSnack(res.error || res.message || "Failed to save", "bg-red-500");
            }
        } catch (e) {
            showSnack(e?.message || e?.error || "Error saving settings", "bg-red-500");
            console.error("[BillingSettings] save failed:", e);
        } finally {
            setSaving(false);
        }
    };

    if (loading || !settings) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div></div>;

    const c = settings.company || {};
    const setC = (patch) => setSettings((prev) => ({ ...prev, company: { ...(prev.company || {}), ...patch } }));
    const bd = settings.bankDetails || {};
    const setBd = (patch) => setSettings((prev) => ({ ...prev, bankDetails: { ...(prev.bankDetails || {}), ...patch } }));
    const setQrImageUrl = (url) => setSettings((prev) => ({ ...prev, qrImageUrl: url }));

    return (
        <div className="max-w-3xl space-y-8">
            <section>
                <h4 className="font-semibold text-gray-800 mb-3">Company Details (shown on invoices & expired screen)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Display Name"><input className="zn-input" value={c.name || ""} onChange={(e) => setC({ name: e.target.value })} /></Field>
                    <Field label="Legal Name"><input className="zn-input" value={c.legalName || ""} onChange={(e) => setC({ legalName: e.target.value })} /></Field>
                    <Field label="Address Line 1"><input className="zn-input" value={c.addressLine1 || ""} onChange={(e) => setC({ addressLine1: e.target.value })} /></Field>
                    <Field label="Address Line 2"><input className="zn-input" value={c.addressLine2 || ""} onChange={(e) => setC({ addressLine2: e.target.value })} /></Field>
                    <Field label="City"><input className="zn-input" value={c.city || ""} onChange={(e) => setC({ city: e.target.value })} /></Field>
                    <Field label="State"><input className="zn-input" value={c.state || ""} onChange={(e) => setC({ state: e.target.value })} /></Field>
                    <Field label="Pincode"><input className="zn-input" value={c.pincode || ""} onChange={(e) => setC({ pincode: e.target.value })} /></Field>
                    <Field label="GSTIN"><input className="zn-input" value={c.gstin || ""} onChange={(e) => setC({ gstin: e.target.value })} /></Field>
                    <Field label="Email"><input className="zn-input" value={c.email || ""} onChange={(e) => setC({ email: e.target.value })} /></Field>
                    <Field label="Phone"><input className="zn-input" value={c.phone || ""} onChange={(e) => setC({ phone: e.target.value })} /></Field>
                    <Field label="Website"><input className="zn-input" value={c.website || ""} onChange={(e) => setC({ website: e.target.value })} /></Field>
                    <div className="sm:col-span-2">
                        <span className="block text-sm font-medium text-gray-600 mb-1">Company Logo</span>
                        <LogoUploader label="Company Logo" value={c.logoUrl} onChange={(url) => setC({ logoUrl: url })} showSnack={showSnack} prefix="subscription-company-logo" />
                        <input className="zn-input mt-2" placeholder="…or paste a logo URL" value={c.logoUrl || ""} onChange={(e) => setC({ logoUrl: e.target.value })} />
                        <p className="text-xs text-gray-400 mt-1">Click <strong>Save Settings</strong> after upload to persist.</p>
                    </div>
                </div>
            </section>

            <section>
                <h4 className="font-semibold text-gray-800 mb-3">Payment Collection</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="UPI ID"><input className="zn-input" value={settings.upiId || ""} onChange={(e) => setSettings((prev) => ({ ...prev, upiId: e.target.value }))} placeholder="yourname@bank" /></Field>
                    <Field label="UPI Display Name"><input className="zn-input" value={settings.upiName || ""} onChange={(e) => setSettings((prev) => ({ ...prev, upiName: e.target.value }))} /></Field>
                    <Field label="Bank Name"><input className="zn-input" value={bd.bankName || ""} onChange={(e) => setBd({ bankName: e.target.value })} /></Field>
                    <Field label="Account Name"><input className="zn-input" value={bd.accountName || ""} onChange={(e) => setBd({ accountName: e.target.value })} /></Field>
                    <Field label="Account Number"><input className="zn-input" value={bd.accountNumber || ""} onChange={(e) => setBd({ accountNumber: e.target.value })} /></Field>
                    <Field label="IFSC"><input className="zn-input" value={bd.ifsc || ""} onChange={(e) => setBd({ ifsc: e.target.value })} /></Field>
                    <div className="sm:col-span-2">
                        <span className="block text-sm font-medium text-gray-600 mb-1">Payment QR Image (shown to clients)</span>
                        <LogoUploader
                            label="QR Image"
                            value={settings.qrImageUrl}
                            onChange={setQrImageUrl}
                            showSnack={showSnack}
                            prefix="subscription-qr"
                            previewClass="h-28 w-28"
                        />
                        <p className="text-xs text-gray-400 mt-1">Upload your UPI / payment QR. Clients will scan this on the renewal screen. Click <strong>Save Settings</strong> after upload.</p>
                    </div>
                </div>
            </section>

            <section>
                <h4 className="font-semibold text-gray-800 mb-3">Defaults & Support</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Default Reminder Days"><input type="number" className="zn-input" value={settings.defaultReminderDays ?? 7} onChange={(e) => setSettings({ ...settings, defaultReminderDays: e.target.value })} /></Field>
                    <Field label="Default Grace Period Days"><input type="number" className="zn-input" value={settings.defaultGracePeriodDays ?? 0} onChange={(e) => setSettings({ ...settings, defaultGracePeriodDays: e.target.value })} /></Field>
                    <Field label="Support Email"><input className="zn-input" value={settings.supportEmail || ""} onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })} /></Field>
                    <Field label="Support Phone"><input className="zn-input" value={settings.supportPhone || ""} onChange={(e) => setSettings({ ...settings, supportPhone: e.target.value })} /></Field>
                    <Field label="Activation SLA Text"><input className="zn-input" value={settings.activationSlaText || ""} onChange={(e) => setSettings({ ...settings, activationSlaText: e.target.value })} /></Field>
                    <Field label="Invoice Prefix"><input className="zn-input" value={settings.invoicePrefix || ""} onChange={(e) => setSettings({ ...settings, invoicePrefix: e.target.value })} /></Field>
                </div>
            </section>

            <div className="flex justify-end">
                <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving..." : "Save Settings"}</button>
            </div>
        </div>
    );
};

/* ============================ small helpers ============================ */
const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-sm font-medium text-gray-600 mb-1">{label}</span>
        {children}
    </label>
);

const Detail = ({ label, value }) => (
    <div className="flex justify-between gap-3 border-b border-gray-100 pb-1.5">
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-800 font-medium text-right">{value || "—"}</span>
    </div>
);

export default SubscriptionManager;
